import { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import {
  QUERY_FOLDERS,
  type QueryFolderType,
} from '@/sections/insights/queryBuilder/savedQueries/folders';
import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import { useSavedQueries } from '@/sections/insights/queryBuilder/savedQueries/queries';
import { SavedQuery } from '@/sections/insights/queryBuilder/types/SavedQuery';

import { QueryFolder } from './QueryFolder';
import { QuerySearch } from './QuerySearch';
import { QuerySearchDropdown } from './QuerySearchDropdown';

/**
 * QueryBuilderSidebar - Folder navigation for saved queries
 *
 * Displays folder categories in accordion format:
 * - Only one folder can be expanded at a time
 * - Shows default + custom queries grouped by folder category
 * - Empty folders show "No queries in this folder" message
 * - Folder expansion state managed locally with useState
 * - Selected query highlight derived from context (single source of truth)
 *
 * When a query is clicked:
 * - Sets workstation mode to 'query' (execution mode)
 * - Updates selectedQuery in context
 *
 * Loading behavior:
 * - Shows loading skeleton while fetching custom queries
 * - Default queries displayed immediately on mount
 * - Error state gracefully degrades to default queries only
 */
export const QueryBuilderSidebar = () => {
  const [expandedFolderId, setExpandedFolderId] =
    useState<QueryFolderType | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounce(
    searchQuery,
    searchQuery === '' ? 0 : 300
  );
  const isSearchActive = searchQuery.length > 0;

  const { queries: queryList, isLoading } = useSavedQueries();

  // Search filtering with prefix/substring ranking
  const searchResults = useMemo(() => {
    if (!debouncedSearch) return [];

    const normalized = debouncedSearch.toLowerCase().trim();
    const prefixMatches: SavedQuery[] = [];
    const substringMatches: SavedQuery[] = [];

    queryList.forEach((query: SavedQuery) => {
      const queryName = query.name.toLowerCase();
      if (queryName.startsWith(normalized)) {
        prefixMatches.push(query);
      } else if (queryName.includes(normalized)) {
        substringMatches.push(query);
      }
    });

    const sortByName = (a: SavedQuery, b: SavedQuery) =>
      a.name.localeCompare(b.name);

    return [
      ...prefixMatches.sort(sortByName),
      ...substringMatches.sort(sortByName),
    ];
  }, [debouncedSearch, queryList]);

  // Alphabetical sorting helper
  const sortQueriesByName = (queries: SavedQuery[]) => {
    return [...queries].sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleFolderClick = (folderId: QueryFolderType) => {
    setExpandedFolderId(expandedFolderId === folderId ? null : folderId);
  };

  // Show loading skeleton while fetching custom queries
  if (isLoading) {
    return (
      <div
        className="bg-chariot-layer1 flex h-full w-[384px] flex-col overflow-y-auto"
        data-testid="query-builder-sidebar-loading"
      >
        <div className="animate-pulse space-y-4 p-4">
          {QUERY_FOLDERS.map(folder => (
            <div key={folder.id} className="space-y-2">
              <div className="bg-chariot-background-secondary h-10 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-chariot-layer1 flex h-full w-[384px] flex-col overflow-y-auto"
      data-testid="query-builder-sidebar"
    >
      <div className="border-chariot-stroke border-b px-6 py-4">
        <h2 className="text-chariot-text-primary text-lg font-bold">
          Saved Queries
        </h2>
      </div>

      {/* Search input */}
      <div className="border-chariot-stroke border-b px-4 py-3">
        <QuerySearch value={searchQuery} onChange={setSearchQuery} />
      </div>

      {/* Conditional: search results OR folders */}
      {isSearchActive ? (
        <QuerySearchDropdown
          isOpen={isSearchActive}
          results={searchResults}
          searchQuery={debouncedSearch}
        />
      ) : (
        QUERY_FOLDERS.map(folderData => {
          const folderQueries = queryList.filter(
            query => query.folderId === folderData.id
          );
          const sortedQueries = sortQueriesByName(folderQueries);

          if (folderData.id === QueryFolders.SECURITY_ADVISORIES) {
            sortedQueries.reverse();
          }

          return (
            <QueryFolder
              key={folderData.id}
              folderData={folderData}
              queries={sortedQueries}
              isExpanded={expandedFolderId === folderData.id}
              onFolderClick={handleFolderClick}
            />
          );
        })
      )}
    </div>
  );
};
